import { BaseWorkflowDefinition, } from './base-workflow-definition.js';
import { FilterField, FilterOperator, OfficeHoursMode, } from './handoff-types.js';
import { ActionType } from '../models/action-type.js';
import { RELATIONSHIP_ACCESS_LEVELS } from '../crm/relationship.js';
import { isValidE164Number } from '../utils/phone-utils.js';
import { z } from 'zod';
// Zod schema for OpenAI structured output
export const HandoffActionSchema = z.object({
    actionDefinition: z.object({
        actionType: z
            .enum([ActionType.PHONE_CALL, ActionType.EMAIL, ActionType.SMS])
            .describe('Type of action to perform'),
    }),
    parameters: z.object({
        number: z
            .string()
            .nullable()
            .optional()
            .describe('Phone number. For PHONE_CALL, can include extension (e.g., 720-600-8781#1). For SMS, no extension.'),
        email: z.string().nullable().optional().describe('Email address'),
    }),
});
export const HandoffActionGroupSchema = z.object({
    name: z
        .string()
        .describe('Concise person/role name (e.g., "Richard Guerrero", "Director of Maintenance")'),
    selectionCriteria: z
        .string()
        .describe('When condition - must start with "When" (e.g., "When asking for Richard, Richard Guerrero, or Director of Maintenance")'),
    actions: z
        .array(HandoffActionSchema)
        .describe('Ordered array of actions to perform'),
});
export const HandoffResponseSchema = z.object({
    actionGroups: z
        .array(HandoffActionGroupSchema)
        .describe('Array of action groups, one for each person/condition mentioned'),
});
export class HandoffWorkflowDefinition extends BaseWorkflowDefinition {
    constructor() {
        super(...arguments);
        this.id = 'handoff';
        this.name = 'Call Handoff Routing';
        this.description = 'Intelligent call handoff routing based on customer requests';
        this.allowedActions = [ActionType.PHONE_CALL, ActionType.EMAIL, ActionType.SMS];
        this.actionType = 'INTERPRETED';
        this.category = 'Call Management';
        this.triggerEventType = 'communication.call.processed';
        this.prompt = `
Convert the handoff instructions into a JSON array of ActionGroup objects. Each ActionGroup should have a clear selectionCriteria and array of actions.

IMPORTANT PARSING RULES:
1. ALWAYS create at least one ActionGroup - never return an empty actionGroups array
2. Create a SEPARATE ActionGroup for EACH person, department, or condition mentioned
3. Parse ALL instructions completely - process every line and every person mentioned
4. GLOBAL RULES: Look for universal actions that apply regardless of the specific person/condition. They will be at the end of the instructions. These are indicated by phrases like:
   - "In all cases..." / "For all cases..." / "Always..." / "Every time..."
   - "Additionally..." / "Also notify..." / "And always..."
   - "Regardless of who..." / "No matter what..."
   These global actions MUST be appended to EVERY ActionGroup's actions array
5. Preserve exact action order - if instructions say "call X, then Y", maintain that sequence in the actions array
6. Phone numbers may include extensions with # - preserve exactly as written (e.g., "720-600-8781#1")
7. Include all name variations in selectionCriteria (e.g., if input says "Ale Williams (or Allie, Ally, Ali)")
8. For SIMPLE CASES of one action group, use "ALWAYS" for the selectionCriteria

CRITICAL KEYWORD RULES FOR ACTION TYPES:
- "forward to {phone}" OR "transfer to {phone}" OR "call {phone}" → PHONE_CALL action (during call)
- "text {phone}" OR "sms {phone}" OR "send sms to {phone}" → SMS action (post-call notification)
- "email {email}" OR "send email to {email}" → EMAIL action (post-call notification)
- "notify {contact}" → DETECT FORMAT to determine action type:
  * If {contact} contains "@" symbol → EMAIL action with parameters.email set to the email address
  * If {contact} is phone number format (digits, dashes, spaces, parentheses) → SMS action with parameters.number set to the phone number
  * This detection is CRITICAL - phone numbers should NEVER go in email parameters and emails should NEVER go in number parameters

PHONE NUMBER HANDLING:
- PHONE_CALL actions: Can include extension with # (e.g., "720-600-8781#1")
- SMS actions: Phone number only, NO extensions (e.g., "720-600-8781")
- Format all phone numbers as: ###-###-####

NAMING CONVENTIONS:
- name: Use the primary person's name or role, keep it concise (e.g., "Richard Guerrero", "Director of Maintenance", NOT the full condition)
- selectionCriteria: Always start with "When" and describe the trigger condition (e.g., "When asking for Richard, Richard Guerrero, or Director of Maintenance")
- Include all name variations, nicknames, and roles in the selectionCriteria

ACTION SEQUENCING:
- Preserve the exact order of actions as specified in the instructions
- "then" or "," between actions means sequential - maintain that order
- Multiple phone numbers should be separate PHONE_CALL or SMS actions in sequence based on keywords
- For each action.parameters object, ALWAYS set the correct field based on action type:
  - PHONE_CALL actions: set "number" to the phone value (with extension if specified) and "email" to null
  - SMS actions: set "number" to the phone value (NO extension) and "email" to null
  - EMAIL actions: set "email" to the email value and "number" to null
  - VALIDATION: Double-check that phone numbers are NEVER in the "email" field and email addresses are NEVER in the "number" field
- Global actions must be appended to the end of each actionGroup.action's array

OUTPUT FORMAT:
Return a JSON object with an "actionGroups" array. Each action group should have:
- name: Concise person/role name
- selectionCriteria: "When..." condition statement
- actions: Array of action objects with actionDefinition and parameters

SIMPLE CASE EXAMPLE (One action group):
Input: "Forward the call to 802-403-5932."
Output:
{
  "actionGroups": [
    {
      "name": "Always forward the call",
      "selectionCriteria": "ALWAYS",
      "actions": [
        {
          "actionDefinition": {"actionType": "PHONE_CALL"},
          "parameters": {"number": "802-403-5932", "email": null}
        }
      ]
    }
  ]
}

COMPLEX CASE EXAMPLE:
Input: "If asking for Richard, Richard Guerrero, Director of Maintenance, forward the call to 840-620-9000#1.
If asking to speak to James Brown, Property Evaluator for Chicago Metro, forward the call to 708-255-3400#1, then 708-255-3401.
If asking for Ale Williams (or Allie, Ally, Ali, Ale), Accounting Admin, forward the call to 303-327-5610#1.
If asking to speak Sarah Smith, Leasing Coordinator, forward the call to 850-524-8000#1, then notify leasing@gmail.com.
In all cases, notify admin@hiresuper.com"

Example output:
{
  "actionGroups": [
    {
      "name": "Richard Guerrero",
      "selectionCriteria": "When asking for Richard, Richard Guerrero, or Director of Maintenance",
      "actions": [
        {
          "actionDefinition": {"actionType": "PHONE_CALL"},
          "parameters": {"number": "840-620-9000#1", "email": null}
        },
        {
          "actionDefinition": {"actionType": "EMAIL"},
          "parameters": {"email": "admin@hiresuper.com", "number": null}
        }
      ]
    },
    {
      "name": "James Brown",
      "selectionCriteria": "When asking to speak to James Brown or Property Evaluator for Chicago Metro",
      "actions": [
        {
          "actionDefinition": {"actionType": "PHONE_CALL"},
          "parameters": {"number": "708-255-3400#1", "email": null}
        },
        {
          "actionDefinition": {"actionType": "PHONE_CALL"},
          "parameters": {"number": "708-255-3401", "email": null}
        },
        {
          "actionDefinition": {"actionType": "EMAIL"},
          "parameters": {"email": "admin@hiresuper.com"}
        }
      ]
    },
    {
      "name": "Ale Williams",
      "selectionCriteria": "When asking for Ale Williams, Allie, Ally, Ali, Ale, or Accounting Admin",
      "actions": [
        {
          "actionDefinition": {"actionType": "PHONE_CALL"},
          "parameters": {"number": "303-327-5610#1", "email": null}
        },
        {
          "actionDefinition": {"actionType": "EMAIL"},
          "parameters": {"email": "admin@hiresuper.com"}
        }
      ]
    },
    {
      "name": "Sarah Smith",
      "selectionCriteria": "When asking to speak to Sarah Smith or Leasing Coordinator",
      "actions": [
        {
          "actionDefinition": {"actionType": "PHONE_CALL"},
          "parameters": {"number": "850-524-8000#1", "email": null}
        },
        {
          "actionDefinition": {"actionType": "EMAIL"},
          "parameters": {"email": "leasing@gmail.com", "number": null}
        },
        {
          "actionDefinition": {"actionType": "EMAIL"},
          "parameters": {"email": "admin@hiresuper.com"}
        }
      ]
    }
  ]
}

EXAMPLE WITH SMS NOTIFICATIONS:
Input: "Forward to Richard at 720-600-8781#1, then notify admin@company.com and text 555-123-4567"

Output:
{
  "actionGroups": [
    {
      "name": "Richard",
      "selectionCriteria": "When asking for Richard",
      "actions": [
        {
          "actionDefinition": {"actionType": "PHONE_CALL"},
          "parameters": {"number": "720-600-8781#1", "email": null}
        },
        {
          "actionDefinition": {"actionType": "EMAIL"},
          "parameters": {"email": "admin@company.com", "number": null}
        },
        {
          "actionDefinition": {"actionType": "SMS"},
          "parameters": {"number": "555-123-4567", "email": null}
        }
      ]
    }
  ]
}

EXAMPLE WITH "NOTIFY" KEYWORD (FORMAT DETECTION):
Input: "Transfer call to 609-922-1579, notify vika@company.com and notify 609-922-1579"

Output:
{
  "actionGroups": [
    {
      "name": "Always",
      "selectionCriteria": "ALWAYS",
      "actions": [
        {
          "actionDefinition": {"actionType": "PHONE_CALL"},
          "parameters": {"number": "609-922-1579", "email": null}
        },
        {
          "actionDefinition": {"actionType": "EMAIL"},
          "parameters": {"email": "vika@company.com", "number": null}
        },
        {
          "actionDefinition": {"actionType": "SMS"},
          "parameters": {"number": "609-922-1579", "email": null}
        }
      ]
    }
  ]
}
IMPORTANT: In the above example, "notify vika@company.com" became EMAIL (contains @) and "notify 609-922-1579" became SMS (phone number format). This format detection is MANDATORY for the "notify" keyword.

Return only valid JSON. Parse ALL people and conditions mentioned in the instructions. Remember to add any global actions to EVERY ActionGroup.
  `.trim();
        this.filters = [
            {
                field: FilterField.PROPERTY_SCOPE,
                description: 'Property scope: match any listed property OR any listed property group',
                required: false,
                allowedOperators: [FilterOperator.IN],
            },
            {
                field: FilterField.GROUP_TYPES,
                description: 'Specific relationship types to filter the workflow scope',
                required: false,
                allowedOperators: [FilterOperator.IN, FilterOperator.NOT_IN],
                allowedValues: RELATIONSHIP_ACCESS_LEVELS,
            },
            {
                field: FilterField.OFFICE_HOURS,
                description: 'Office hours filter',
                required: false,
                allowedValues: Object.values(OfficeHoursMode),
                allowedOperators: [FilterOperator.EQUALS],
            },
        ];
    }
    validate(workflow) {
        // Call base validation first
        const baseResult = super.validate(workflow);
        const errors = [...baseResult.errors];
        const warnings = [...(baseResult.warnings || [])];
        // Validate handoff-specific parameters
        if (workflow.actionGroups) {
            for (const group of workflow.actionGroups) {
                if (group.actions) {
                    for (const action of group.actions) {
                        const actionType = action.actionDefinition?.actionType;
                        const params = action.parameters;
                        if (actionType === ActionType.PHONE_CALL) {
                            if (!params?.number) {
                                errors.push(`Phone call action missing required "number" parameter`);
                            }
                            else if (typeof params.number !== 'string') {
                                errors.push(`Phone call "number" parameter must be a string`);
                            }
                            else if (!isValidE164Number(params.number)) {
                                warnings.push(`Phone number "${params.number}" may not be in valid E.164 format (+1234567890)`);
                            }
                        }
                        else if (actionType === ActionType.SMS) {
                            if (!params?.number) {
                                errors.push(`SMS action missing required "number" parameter`);
                            }
                            else if (typeof params.number !== 'string') {
                                errors.push(`SMS "number" parameter must be a string`);
                            }
                            else {
                                const phoneNumber = params.number;
                                // Check for extension (not allowed for SMS)
                                if (phoneNumber.includes('#')) {
                                    errors.push(`SMS action cannot have extension: "${phoneNumber}". Extensions are only for PHONE_CALL actions.`);
                                }
                                // Basic phone number format validation
                                if (!isValidE164Number(phoneNumber)) {
                                    warnings.push(`Phone number "${phoneNumber}" may not be in valid E.164 format (+1234567890)`);
                                }
                            }
                        }
                        else if (actionType === ActionType.EMAIL) {
                            if (!params?.email) {
                                errors.push(`Email action missing required "email" parameter`);
                            }
                            else if (typeof params.email !== 'string') {
                                errors.push(`Email "email" parameter must be a string`);
                            }
                            else if (!this.isValidEmail(params.email)) {
                                errors.push(`Invalid email address: "${params.email}"`);
                            }
                            // Optional subject validation
                            if (params?.subject && typeof params.subject !== 'string') {
                                errors.push(`Email "subject" parameter must be a string`);
                            }
                        }
                    }
                }
            }
        }
        return {
            isValid: errors.length === 0,
            errors,
            warnings: warnings.length > 0 ? warnings : undefined,
        };
    }
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.toLowerCase());
    }
}
export const handoffDefinition = new HandoffWorkflowDefinition();
//# sourceMappingURL=handoff.js.map